import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  setTicketDetail,
  tickets,
  ticketKeys,
  type Ticket,
  type TicketRecoveryReason,
  type UpdateTicketData,
} from '@field-tickets/shared';
import { showToast } from '@/components/Toast';
import { getAuthEpoch, isCurrentAuthEpoch } from '@/utils/authEpoch';
import {
  discardTicketSave,
  getAllQueuedTicketSaves,
  withTicketSaveLock,
  type TicketConflictSnapshot,
} from '@/utils/offlineQueue';
import {
  buildTicketRecoveryDraft,
  clearTicketRecoveryDrafts,
  isTicketRecoveryStorageKey,
  listTicketRecoveryDrafts,
  ticketRecoveryDraftsMemoryOnly,
  writeTicketRecoveryDraft,
  RECOVERY_CLOSE_WARNING,
  TICKET_RECOVERY_STORAGE_WARNING,
  TICKET_RECOVERY_EVENT,
  TICKET_RECOVERY_TAB_ID,
  type TicketRecoveryDraft,
} from '@/utils/ticketRecovery';
import { retainTicketRecoveryDrafts } from '@/utils/ticketRetainedDrafts';

/** What the recovery panel shows. */
export interface TicketRecoveryView {
  reason: TicketRecoveryReason;
  message: string;
  changedFields: string[];
  /** Every stored draft's edits, as copyable text. */
  unsavedSummary: string;
  /** How many drafts (tabs or sessions) hold edits for this ticket. */
  draftCount: number;
  /** Set when the last reload attempt failed. */
  reloadError?: string;
  /** Set when a draft could be kept only until this tab closes. */
  storageWarning?: string;
}

export interface TicketRecoveryOptions {
  /** Session that owns the form's edits; never recaptured at cleanup. */
  sessionEpoch: number;
  /**
   * The form's whole current payload (pinned to its base version), and
   * whether it holds edits the server has not confirmed.
   */
  currentEdits: () => {
    payload: UpdateTicketData;
    baseSnapshot?: TicketConflictSnapshot;
    /** The payload the form held for its base version (#1041). */
    basePayload?: Record<string, unknown>;
    dirty: boolean;
  };
  /**
   * Called on every entry. `hydrate` is true when the draft came from
   * storage (reopen, offline replay, another tab) and the form should show
   * its edits.
   */
  onEnter: (draft: TicketRecoveryDraft, options: { hydrate: boolean }) => void;
  /** Replace the form with the confirmed fresh ticket. */
  adopt: (ticket: Ticket) => void;
}

const RELOAD_FAILED_MESSAGE =
  "Couldn't reach the server to reload the ticket. Your unsaved edits are kept below. Try again.";
const RELOAD_STORAGE_FULL_MESSAGE =
  "This browser's storage is full, so reloading now would keep your unsaved edits only until this tab closes. Nothing changed. Copy your edits below, then try again.";

function primaryDraft(drafts: TicketRecoveryDraft[]): TicketRecoveryDraft | undefined {
  return drafts.find((d) => d.tabId === TICKET_RECOVERY_TAB_ID) ?? drafts.at(-1);
}

function viewOf(drafts: TicketRecoveryDraft[], reloadError?: string, memoryOnly = false): TicketRecoveryView | null {
  const primary = primaryDraft(drafts);
  if (!primary) return null;
  const unsavedSummary = drafts.length === 1
    ? primary.summary
    : drafts
      .map((d) => {
        const source = d.tabId === TICKET_RECOVERY_TAB_ID ? 'This tab' : 'Another tab or an earlier session';
        return `${source} (${new Date(d.createdAt).toLocaleString()}):\n${d.summary}`;
      })
      .join('\n\n');
  return {
    reason: primary.reason,
    message: primary.message,
    changedFields: [...new Set(drafts.flatMap((d) => d.changedFields))],
    unsavedSummary,
    draftCount: drafts.length,
    reloadError,
    storageWarning: memoryOnly ? TICKET_RECOVERY_STORAGE_WARNING : undefined,
  };
}

/**
 * The web ticket recovery state machine (#967). One per open ticket form;
 * every write path enters it the same way.
 *
 * - `enter(reason)`: a save, status change, QuickBooks send or replay ended in
 *   recovery. Writes this tab's durable draft first (the form's latest edits
 *   plus the summary), then goes sticky: the form must block edits and every
 *   save path while `recoveringRef.current` is true (`isBlocked()` also
 *   picks up a draft another tab or the replay just wrote).
 * - Restore: any draft of this ticket for this user (closed or hidden ticket,
 *   page reload, offline replay, another tab) puts the form in recovery. A
 *   dirty form gets its own draft next to the others; it never overwrites one.
 * - `reload()`: the only exit, on the user's explicit choice. A confirmed
 *   network read (`tickets.getFresh`, never `refetch()` or a cache) under the
 *   save lock; in-flight ticket reads are cancelled and the cache seeded with
 *   the fresh ticket (no refetch follows). If the form still shows the same
 *   ticket, every draft's edits move to the ticket's retained drafts (#1041:
 *   offered for an explicit restore, never sent; if they can't be stored
 *   durably the reload stops there and says so), it adopts the ticket, and
 *   the recovery drafts and its queued save are cleared; if the form moved on
 *   meanwhile, only the cache is seeded. On failure nothing changes but the
 *   error text. A reload that finishes after a sign-out or a user switch
 *   (the session epoch moved) touches nothing: not the cache, the drafts,
 *   the form or the queue.
 */
export function useTicketRecovery(ticketId: string, owner: string | null, options: TicketRecoveryOptions) {
  const queryClient = useQueryClient();
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;

  const [drafts, setDrafts] = useState<TicketRecoveryDraft[]>(() => listTicketRecoveryDrafts(owner, ticketId));
  const [reloadError, setReloadError] = useState<string | undefined>(undefined);
  const [reloading, setReloading] = useState(false);
  // Mirrors `drafts.length > 0` synchronously for guards in callbacks/effects.
  const recoveringRef = useRef(drafts.length > 0);
  const reloadingRef = useRef(false);
  // Moves on every ticket/owner change and unmount: a reload that started for
  // an earlier ticket must not touch this form.
  const generationRef = useRef(0);

  const activate = useCallback((next: TicketRecoveryDraft[], hydrate: boolean) => {
    const primary = primaryDraft(next);
    if (!primary) return;
    recoveringRef.current = true;
    setDrafts(next);
    setReloadError(undefined);
    optionsRef.current.onEnter(primary, { hydrate });
  }, []);

  // Follow the drafts in storage: restore on open, and entries made outside
  // this form (the replay, the close-time flush, another tab).
  const syncFromStorage = useCallback(() => {
    if (!isCurrentAuthEpoch(optionsRef.current.sessionEpoch)) return;
    const currentOwner = ownerRef.current;
    const stored = listTicketRecoveryDrafts(currentOwner, ticketId);
    // Cleared elsewhere (another tab's reload): this form keeps its state
    // until its own reload; its edits are still on screen.
    if (stored.length === 0) return;
    if (recoveringRef.current) {
      setDrafts(stored);
      return;
    }
    const edits = optionsRef.current.currentEdits();
    if (edits.dirty && currentOwner) {
      // This form holds newer edits than any stored draft: they get this
      // tab's own draft, next to the others, never over them.
      const newest = stored[stored.length - 1];
      recoveringRef.current = true;
      writeTicketRecoveryDraft(buildTicketRecoveryDraft(
        currentOwner,
        ticketId,
        newest.reason,
        edits.payload,
        edits.baseSnapshot ?? newest.baseSnapshot,
        newest.changedFields,
        edits.baseSnapshot ? edits.basePayload : newest.basePayload,
      ));
      activate(listTicketRecoveryDrafts(currentOwner, ticketId), false);
      return;
    }
    activate(stored, true);
  }, [activate, ticketId]);

  useEffect(() => {
    if (!ticketId) return undefined;
    generationRef.current += 1;
    recoveringRef.current = false;
    reloadingRef.current = false;
    setReloading(false);
    setReloadError(undefined);
    setDrafts([]);
    syncFromStorage();
    const onRecoveryEvent = (event: Event) => {
      if ((event as CustomEvent<{ ticketId?: string }>).detail?.ticketId === ticketId) syncFromStorage();
    };
    const onStorage = (event: StorageEvent) => {
      if (ownerRef.current && isTicketRecoveryStorageKey(event.key, ownerRef.current, ticketId)) syncFromStorage();
    };
    window.addEventListener(TICKET_RECOVERY_EVENT, onRecoveryEvent);
    window.addEventListener('storage', onStorage);
    return () => {
      generationRef.current += 1;
      window.removeEventListener(TICKET_RECOVERY_EVENT, onRecoveryEvent);
      window.removeEventListener('storage', onStorage);
    };
  }, [syncFromStorage, ticketId, owner]);

  const enter = useCallback((reason: TicketRecoveryReason, changedFields: string[] = []) => {
    if (!ticketId || !isCurrentAuthEpoch(optionsRef.current.sessionEpoch)) return;
    const currentOwner = ownerRef.current;
    if (recoveringRef.current) {
      // Already recovering: the first entry's draft holds the edits; only add
      // what the server changed, when this answer knows it.
      const own = listTicketRecoveryDrafts(currentOwner, ticketId).find((d) => d.tabId === TICKET_RECOVERY_TAB_ID);
      if (own && changedFields.length > 0) writeTicketRecoveryDraft({ ...own, changedFields });
      const stored = listTicketRecoveryDrafts(currentOwner, ticketId);
      if (stored.length > 0) setDrafts(stored);
      return;
    }
    const edits = optionsRef.current.currentEdits();
    const next = buildTicketRecoveryDraft(
      currentOwner ?? '',
      ticketId,
      reason,
      edits.payload,
      edits.baseSnapshot,
      changedFields,
      edits.basePayload,
    );
    // Durable first, so closing or hiding the ticket right now keeps it.
    recoveringRef.current = true;
    if (currentOwner) writeTicketRecoveryDraft(next);
    showToast(next.message, 'warning');
    const stored = listTicketRecoveryDrafts(currentOwner, ticketId);
    activate(stored.length > 0 ? stored : [next], false);
  }, [activate, ticketId]);

  /**
   * For save paths, inside the save lock right before a request: true when
   * the ticket is in recovery, including a draft written while they waited.
   */
  const isBlocked = useCallback((): boolean => {
    if (!recoveringRef.current) syncFromStorage();
    return recoveringRef.current;
  }, [syncFromStorage]);

  const reload = useCallback(async () => {
    // Everything this reload acts on is captured now: the form may move to
    // another ticket while the read is in flight.
    const id = ticketId;
    const currentOwner = ownerRef.current;
    const generation = generationRef.current;
    const epoch = optionsRef.current.sessionEpoch;
    const adopt = optionsRef.current.adopt;
    if (!id || !isCurrentAuthEpoch(epoch) || !recoveringRef.current || reloadingRef.current) return;
    const sameSession = () => getAuthEpoch() === epoch;
    const stillHere = () => generationRef.current === generation && sameSession();
    reloadingRef.current = true;
    setReloading(true);
    setReloadError(undefined);
    let adopted = false;
    let kept = 0;
    let notDurable = false;
    try {
      // Under the save lock, so an offline replay of this ticket finishes
      // before the read instead of landing after it.
      await withTicketSaveLock(id, async () => {
        if (!sameSession()) return;
        // Reload discards only the queue admission present when it started.
        const queued = (await getAllQueuedTicketSaves(currentOwner)).filter((entry) => entry.ticketId === id);
        if (!sameSession()) return;
        const fresh = await tickets.getFresh(id);
        if (!sameSession()) return;
        // A ticket read that started earlier could still land an older copy
        // over this one: cancel it, then seed (never below a newer revision).
        await queryClient.cancelQueries({ queryKey: ticketKeys.detail(id) });
        if (!sameSession()) return;
        // Refused (null) once the session moved since Reload was pressed.
        const current = setTicketDetail(queryClient, id, fresh, epoch);
        if (!current || !stillHere()) return;
        // Reading the latest must not delete the only copy of unsent edits:
        // they are stored as retained drafts first, durably or not at all.
        const retained = retainTicketRecoveryDrafts(listTicketRecoveryDrafts(currentOwner, id));
        if (!retained.durable) {
          notDurable = true;
          return;
        }
        kept = retained.kept;
        adopt(current);
        clearTicketRecoveryDrafts(currentOwner, id);
        await Promise.all(queued.map((entry) => discardTicketSave(entry).catch(() => undefined)));
        adopted = true;
      });
      if (notDurable && stillHere()) setReloadError(RELOAD_STORAGE_FULL_MESSAGE);
      if (!adopted || !stillHere()) return;
      recoveringRef.current = false;
      setDrafts([]);
      showToast(kept > 0
        ? 'Ticket reloaded. Your unsent edits are kept on this device: restore or discard them from the notice on the ticket.'
        : 'Ticket reloaded. Check it and re-enter any edits that are missing.', 'info');
    } catch {
      if (stillHere()) setReloadError(RELOAD_FAILED_MESSAGE);
    } finally {
      // Only this form's own flag: the reload is over either way.
      if (generationRef.current === generation) {
        reloadingRef.current = false;
        setReloading(false);
      }
    }
  }, [queryClient, ticketId]);

  // Leaving the page while recovering: the drafts are kept, but say so.
  const recovering = drafts.length > 0;
  useEffect(() => {
    if (!recovering) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = RECOVERY_CLOSE_WARNING;
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [recovering]);

  return {
    recovery: viewOf(drafts, reloadError, drafts.length > 0 && ticketRecoveryDraftsMemoryOnly(owner, ticketId)),
    recoveringRef,
    isReloading: reloading,
    enter,
    isBlocked,
    reload,
  };
}
